import axios from "axios";
import React from 'react';
import { BsPlusCircle } from 'react-icons/bs'
import CampLayout from './common/CampLayout';
import KidInfo from "./KidInfo";
import { useNavigate } from 'react-router-dom';
import { checkIfLoggedIn } from '../services/utilService';
import { toast } from 'react-toastify';
import { Loader, Button, Modal } from 'rsuite';

const apiUrl = process.env.REACT_APP_SERVER_API_URL;

// Soft pastel avatar palettes [background, text]; picked deterministically from the name
const AVATAR_COLORS = [
    ['#DBEAFE', '#1E40AF'],
    ['#FCE7F3', '#9D174D'],
    ['#D1FAE5', '#065F46'],
    ['#FEF3C7', '#92400E'],
    ['#EDE9FE', '#5B21B6'],
    ['#FFE4E6', '#9F1239'],
];

const avatarFor = (kid) => {
    const name = `${kid.firstName || ''} ${kid.lastName || ''}`;
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) % 9973;
    const [bg, fg] = AVATAR_COLORS[hash % AVATAR_COLORS.length];
    const initials = `${(kid.firstName || ' ')[0]}${(kid.lastName || ' ')[0]}`.toUpperCase().trim();
    return { bg, fg, initials };
};

const ManageKids = () => {
    const [kids, setKids] = React.useState([]);
    const [kid, setKid] = React.useState(null);
    const [createKid, setCreateKid] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);

    function removeKid(kidToRemove) {
        axios.delete(`${apiUrl}/kids/${kidToRemove._id}`);

        setKids(kids.filter((item) => item._id !== kidToRemove._id));
        setKid(null);
    }

    function createOrUpdateKid(kidId, kidData) {
        if (kidId) {
            axios.put(`${apiUrl}/kids/${kidId}`, kidData)
                .then((response) => {
                    setKids([...kids.filter((item) => item._id !== response.data._id), response.data]
                        .sort((a, b) => a.createdDate > b.createdDate ? 1 : -1));
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            axios.post(`${apiUrl}/kids`, kidData)
                .then((response) => {
                    if (kids.length === 0) toast.success('Great! You can now book camps for your child.', {});
                    setKids([...kids, response.data]);
                })
                .catch((error) => {
                    console.log(error);
                })
        };
    }

    function getAge(dateString) {
        var today = new Date();
        var birthDate = new Date(dateString);
        var age = today.getFullYear() - birthDate.getFullYear();
        var m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }

    const navigate = useNavigate();

    React.useEffect(() => {
        checkIfLoggedIn();

        if (!localStorage.getItem("user-id")) {
            navigate("/login");
        } else {
            let mounted = true;
            setIsLoading(true);
            axios.get(`${apiUrl}/users/${localStorage.getItem('user-id')}/kids`)
                .then((response) => {
                    if (mounted) {
                        setKids(response.data
                            .sort((a, b) => a.createdDate > b.createdDate ? 1 : -1)
                        );
                    }
                })
                .finally(() => {
                    if (mounted) {
                        setIsLoading(false);
                    }
                });
            return () => mounted = false;
        }
    }, [navigate]);

    return (
        <CampLayout page="manage">
            <div>
                <h1 className="page-title ml-7 mb-3">Manage Kids</h1>
                {!isLoading && kids.length === 0 ?
                    <p className="text-base ml-7">Manage your children's profiles here. Please add at least one child to be able to book a camp.</p>
                    : !isLoading ?
                    <div className="ml-7">
                        <Button appearance="primary" onClick={() => navigate('/book')}>
                            Book a camp →
                        </Button>
                    </div>
                    : null
                }

                {isLoading ? (
                    <div className="flex justify-center items-center p-8">
                        <Loader size="lg" content="Loading kids..." />
                    </div>
                ) : (
                    <div className="flex flex-wrap p-7 gap-6 w-full max-w-full">
                    {kids.map((kid) => {
                        const { bg, fg, initials } = avatarFor(kid);
                        return (
                        <div
                            className="cursor-pointer w-full rounded-xl border-gray-200 border-1 shadow bg-white hover:shadow-md transition-shadow p-5 lg:w-96 flex flex-col"
                            onClick={() => setKid(kid)}
                            key={kid._id}
                        >
                            <div className="flex items-center gap-4">
                                <div
                                    className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0"
                                    style={{ backgroundColor: bg, color: fg }}
                                    aria-hidden="true"
                                >
                                    {initials}
                                </div>
                                <div>
                                    <div className="text-lg font-semibold text-gray-900">{kid.firstName} {kid.lastName}</div>
                                    <div className="text-sm text-gray-600">{getAge(kid.dob)} years old</div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                                <Button
                                    appearance="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); setKid(kid); }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    appearance="subtle"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(kid); }}
                                >
                                    Delete
                                </Button>
                            </div>
                        </div>
                        );
                    })}
                    <div
                        className="cursor-pointer w-full rounded-xl border-gray-200 border-1 shadow bg-white hover:shadow-md transition-shadow lg:w-96"
                        onClick={() => setCreateKid(true)}
                        role="button"
                        aria-label="Add a child"
                    >
                        <div className="flex flex-col items-center justify-center gap-2 w-full h-full py-8 text-gray-600" style={{ minHeight: '8rem' }}>
                            <BsPlusCircle size={40} />
                            <span className="text-sm font-medium">Add a child</span>
                        </div>
                    </div>
                </div>
                )}
            </div>

            {kid && <KidInfo kid={kid} setKid={setKid} removeKid={removeKid} createOrUpdateKid={createOrUpdateKid} />}
            {createKid && <KidInfo createKid={createKid} setCreateKid={setCreateKid} createOrUpdateKid={createOrUpdateKid} />}

            {/* Delete confirmation (calls the existing removeKid handler) */}
            <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="xs">
                <Modal.Header>
                    <Modal.Title>Remove {deleteTarget?.firstName}'s profile?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This will remove {deleteTarget?.firstName} {deleteTarget?.lastName} from your account.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="red"
                        appearance="primary"
                        onClick={() => { removeKid(deleteTarget); setDeleteTarget(null); }}
                    >
                        Delete
                    </Button>
                    <Button appearance="subtle" onClick={() => setDeleteTarget(null)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        </CampLayout>

    )
}

export default ManageKids;
